/* eslint max-depth: ["error", 3] */
import { captureException } from '@chilipiper/sentry'
import { getValue } from '@chilipiper/config'
import { Element } from '@xmpp/xml'
import { TimeoutError } from '@xmpp/events'
import * as R from 'remeda'
import { nanoid } from 'nanoid'
import invariant from 'tiny-invariant'
import { Affiliations, Marker, Room } from '../chat-data/types'
import { exponentialSleep } from '../helpers/exponentialSleep'
import { escapeEvent } from '../helpers/event-escaping/eventEscaping'
import {
  DEFAULT_MAX_ROOMS_INBOX_CHUNK,
  INBOX_XMLNS,
  MAM_XMLNS,
  MARKERS_XMLNS,
  MUC_DOMAIN,
  MUC_LIGHT_AFFILIATIONS,
  PAGINATION_XMLNS,
  SM_XMLNS,
} from './constants'
import {
  onAffiliationsStanza,
  onArchivedAffiliationsStanza,
  onArchivedGroupchatMarkerStanza,
  onArchiveGroupchatStanza,
  onConversationStatusChange,
  onFinInboxStanza,
  onGroupchatFailedStanza,
  onGroupchatMarkerStanza,
  onGroupchatStanza,
  onInboxStanza,
} from './listeners'
import { parseInboxItemStanza } from './parsers/inboxStanza'
import { PayloadIn, TypedEnvelopeOut } from './types'
import { client as createXMPPClient, xml } from './xmpp-lib'

export interface Credentials {
  refreshToken?: string
  token: string
  username: string
}

export type Status = 'CONNECTED' | 'CONNECTING' | 'RECONNECTING' | 'ERROR' | 'DISCONNECTED'
export type EventStatus = 'displayed' | 'failed'

export interface Listeners {
  onConversationStatusChange?: (event: Extract<PayloadIn, { type: 'StatusChange' }>) => void
  onGroupchatMarker?: (marker: Marker) => void
  onGroupchatMessage?: (event: PayloadIn) => void
  onGroupchatMessageFailed?: (event: Extract<PayloadIn, { type: 'Error' }>) => void
  onAffiliationChange?: (event: Affiliations) => void
  onArchivedAffiliationsChange?: (event: Affiliations) => void
}

export type XMPPClientType = ReturnType<typeof createClient>

export const createClient = (id: string) => {
  type Client = ReturnType<typeof createXMPPClient>
  let client: Client | undefined

  let lastJid: Client['jid'] | null = null
  let messagesQueue: Array<{
    element: Element
    onError: (error: Error) => void
    onSuccess: (element: Element) => void
    type: 'element' | 'iq'
  }> = []

  const getJid = (client: Client | undefined) => {
    if (!client?.jid) {
      return lastJid
    }
    lastJid = client.jid
    return client.jid
  }

  const sendElementRaw = async (type: 'element' | 'iq', element: Element) => {
    invariant(client, 'Authenticate before sending element')
    if (type === 'iq') {
      try {
        return await client.iqCaller.request(element)
      } catch (error) {
        // Ignore timeout errors because:
        // 1. We send IQ and connection closes
        // 2. We retry the IQ in next connection and get a response
        // @xmpp library throws TimeoutError anyway
        // eslint-disable-next-line max-depth
        if (error instanceof Error && error.name === 'TimeoutError') {
          // never resolve because we will retry the message
          return new Promise<Element>(() => {})
        }
        console.error(error)
        throw error
      }
    }
    try {
      await client.send(element)
    } catch (error) {
      console.error(error)
      throw error
    }
    return element
  }
  const sendElementWithRetry = (type: 'element' | 'iq', element: Element) => {
    return new Promise<Element>((resolve, reject) => {
      if (!client) {
        return reject(Error('Authenticate before sending element'))
      }
      if (client.status === 'online') {
        const id = element.getAttr('id')
        if (id) {
          const retryTimeoutId = setTimeout(() => {
            sendElementRaw(type, element).catch(rejectWithCleanup)
          }, 15 * 1000)
          const rejectTimeoutId = setTimeout(() => {
            rejectWithCleanup(Error('Element timeout'))
          }, 30 * 1000)

          const cleanup = () => {
            clearTimeout(retryTimeoutId)
            clearTimeout(rejectTimeoutId)
            client?.removeListener('close', handleClientConnectionClose)
            client?.removeListener('stanza', resolveWithClosingStanza)
          }
          const rejectWithCleanup = (error: Error) => {
            cleanup()
            reject(error)
          }
          const handleClientConnectionClose = () => {
            cleanup()
            // no need to reject because we're already calling onError it for all messages in messagesQueue
          }
          client.addListener('close', handleClientConnectionClose)
          const resolveWithClosingStanza = (stanza: Element) => {
            if (stanza.getAttr('id') === id) {
              cleanup()
              resolve(stanza)
            }
          }
          client.addListener('stanza', resolveWithClosingStanza)

          sendElementRaw(type, element).catch(rejectWithCleanup)
        } else {
          sendElementRaw(type, element).then(resolve).catch(reject)
        }
      }
    })
  }

  /** True from when client went offline until queued messages are sent, False otherwise */
  let isSendingNextOldQueuedMessages = true

  const sendElement = async (
    type: 'element' | 'iq',
    element: Element,
    options?: { sendFirst?: boolean }
  ) => {
    return new Promise<Element>((resolve, reject) => {
      const message = {
        type,
        element,
        onSuccess: resolve,
        onError: reject,
      }
      if (options?.sendFirst) {
        messagesQueue.unshift(message)
      } else {
        messagesQueue.push(message)
      }
      // The first sendElement call will start the queue
      if (messagesQueue.length > 1) {
        return
      }

      // We don't want to send if we're pending queued messages because it could lead to duplicates
      if (!isSendingNextOldQueuedMessages) {
        sendAndEmptyMessagesQueue()
      }
    })
  }

  let latestCall = 0
  const sendAndEmptyMessagesQueue = async (cb?: () => void) => {
    latestCall += 1
    const thisCall = latestCall
    while (messagesQueue.length > 0) {
      const messagesQueueCopy = [...messagesQueue]
      await Promise.all(
        messagesQueueCopy.map(async message => {
          const { element, onSuccess, onError, type } = message
          try {
            onSuccess(await sendElementWithRetry(type, element))
          } catch (error) {
            console.error(error)
            if (error instanceof Error) {
              onError(error)
            } else {
              onError(Error(String(error)))
            }
          }
          messagesQueue = messagesQueue.filter(el => el !== message)
        })
      )
      // To prevent sending the same messages again
      if (thisCall !== latestCall) {
        break
      }
    }
    cb?.()
  }

  const auth = (credentials: Credentials, tenantId: string) => {
    const origin = typeof window !== 'undefined' ? window.origin : ''
    client = createXMPPClient({
      service: `${origin.replace(
        'http',
        'ws'
      )}/api/im/public/tenant/${tenantId}/websockets/?id=${id}`,
      domain: 'localhost',
      credentials,
    })
    client.setMaxListeners(100)
    client.on('disconnect', () => {
      isSendingNextOldQueuedMessages = true
    })

    let reconnectionTimeout: ReturnType<typeof setTimeout> | undefined

    client.on('connect', () => {
      // Load balancer kills connection every REACT_APP_XMPP_TIMEOUT_SECONDS seconds, but sometimes we still think we're connected.
      // Make sure we use a working connection
      clearTimeout(reconnectionTimeout)
      reconnectionTimeout = setTimeout(
        async () => {
          try {
            await client?.disconnect()
          } catch (e) {
            if (e instanceof TypeError) {
              // It's most likely an issue in xmpp library, we should handle it gracefully as socket is probably already closed
              return
            }
            if (e instanceof TimeoutError) {
              // The socket is likely already closed the close event inside xmpp library wasn't emitted before it timed out making throw here
              // https://github.com/xmppjs/xmpp.js/blob/ee361dc968cde84dfb4295d840c534d36bcecfc3/packages/connection/index.js#L245
              return
            }
            throw e
          }
        },
        Number(getValue('REACT_APP_XMPP_TIMEOUT_SECONDS') || 60) * 1000
      )
    })

    client.on('element', element => {
      invariant(client, 'Authenticate before listening to elements')
      // Resumed is sent when resumed stream
      // Enabled is sent when established a new stream
      if (
        (element.name === 'resumed' || element.name === 'enabled') &&
        element.getAttr('xmlns') === SM_XMLNS
      ) {
        handleNewWebsocketConnection()
      }
    })
  }

  const handleNewWebsocketConnection = () => {
    // We need to schedule a task so status can switch from open to online
    setTimeout(async () => {
      invariant(client, 'Authenticate before listening to send')
      if (client.status === 'online') {
        await sendAndEmptyMessagesQueue(() => {
          isSendingNextOldQueuedMessages = false
        })
      } else {
        captureException(
          new Error(
            `Tried retransmitting messages queue, but status is not online got: ${client.status}`
          )
        )
      }
    }, 0)
  }

  const loadInbox = async (params?: {
    after?: string
    before?: string
    box?: 'inbox' | 'archive' | 'all'
    max?: number
  }) => {
    invariant(client, 'Authenticate before loading inbox')
    const defaultParams = {
      box: 'inbox',
      max: DEFAULT_MAX_ROOMS_INBOX_CHUNK,
    }

    const mergedParams = {
      ...defaultParams,
      ...params,
    }

    const pagination = [xml('max', {}, mergedParams.max.toString())]

    if (mergedParams.after) {
      pagination.push(xml('after', {}, mergedParams.after))
    }

    if (mergedParams.before) {
      pagination.push(xml('before', {}, mergedParams.before))
    }
    const currentLoadInboxId = nanoid()

    const query = xml(
      'iq',
      { type: 'set', id: currentLoadInboxId },
      xml(
        'inbox',
        { xmlns: INBOX_XMLNS, queryid: currentLoadInboxId },
        xml(
          'x',
          { xmlns: 'jabber:x:data', type: 'form' },
          xml('field', { var: 'FORM_TYPE', type: 'hidden' }, xml('value', {}, INBOX_XMLNS)),
          xml('field', { var: 'box', type: 'list-single' }, xml('value', {}, mergedParams.box))
        ),
        xml('set', PAGINATION_XMLNS, ...pagination)
      )
    )

    let rooms: Array<Room> | undefined
    const unsubscribe = onInboxStanza(client, room => {
      if (room.queryId === currentLoadInboxId) {
        if (rooms === undefined) {
          rooms = []
        }
        rooms.push(room)
      }
    })
    const unsubscribeFinInboxStanza = onFinInboxStanza(client, ({ queryId, lastId }) => {
      if (queryId === currentLoadInboxId) {
        if (rooms === undefined) {
          rooms = []
        } else {
          rooms = rooms.map(room =>
            room.queryId === queryId
              ? {
                  ...room,
                  lastId,
                }
              : room
          )
        }
      }
    })
    await sendElement('iq', query)
    unsubscribe()
    unsubscribeFinInboxStanza()
    return rooms
  }

  const loadInboxItem = async (roomId: string) => {
    invariant(client, 'Authenticate before loading inbox item')
    const query = xml(
      'iq',
      { type: 'get' },
      xml('query', {
        xmlns: `${INBOX_XMLNS}#conversation`,
        jid: `${roomId}@${MUC_DOMAIN}`,
        complete: true,
      })
    )
    const stanza = await sendElement('iq', query)
    return parseInboxItemStanza(stanza)
  }

  /**
   * @param params.max - The maximum number of events to load. If number of loaded events is less
   *   than max, it means that there are no more events to load.
   * @returns
   */
  const loadRoomEvents = async (
    roomId: string,
    params?: { after?: string; before?: string; max?: number; waitForRoomPreparation?: boolean }
  ) => {
    invariant(client, 'Authenticate before loading room events')
    const defaultParams = {
      max: 100,
    }

    const mergedParams = {
      ...defaultParams,
      ...params,
    }

    const pagination = [xml('max', {}, mergedParams.max.toString()), xml('before')]

    if (mergedParams.after) {
      pagination.push(xml('after', {}, mergedParams.after))
    }

    if (mergedParams.before) {
      pagination.push(xml('before', {}, mergedParams.before))
    }

    const query = xml(
      'iq',
      { type: 'set', to: `${roomId}@${MUC_DOMAIN}` },
      xml(
        'query',
        MAM_XMLNS,
        xml('x', 'jabber:x:data', xml('field', { var: 'simple' }, xml('value', {}, 'true'))),
        xml('set', PAGINATION_XMLNS, ...pagination)
      )
    )

    const events: Array<PayloadIn | Marker | Affiliations> = []

    const unsubscribe = onArchiveGroupchatStanza(
      client,
      event => {
        events.push(event)
      },
      stanza => stanza.getAttr('from') === `${roomId}@${MUC_DOMAIN}`
    )

    const unsubscribeMarkers = onArchivedGroupchatMarkerStanza(
      client,
      marker => {
        events.push(marker)
      },
      stanza => stanza.getAttr('from') === `${roomId}@${MUC_DOMAIN}`
    )

    const unsubscribeAffiliations = onArchivedAffiliationsStanza(
      client,
      event => {
        events.push(event)
      },
      stanza => stanza.getAttr('from') === `${roomId}@${MUC_DOMAIN}`
    )

    // When using pagination it's possible there are no messages left to fetch
    if (params?.before === undefined && params?.waitForRoomPreparation) {
      // We need to pool because it's possible we send this iq before BE prepares the room
      for (let i = 0; i < 8; i++) {
        if (i > 0) {
          // min 200ms, max 12.8s (total 25.4s)
          await exponentialSleep(i, 100)
        }
        await sendElement('iq', query)
        if (events.length > 0) {
          break
        }
      }
    } else {
      await sendElement('iq', query)
    }

    unsubscribe()
    unsubscribeMarkers()
    unsubscribeAffiliations()

    return events
  }

  const sendEvent = async (to: string, event: TypedEnvelopeOut, eventId = nanoid()) => {
    const jid = getJid(client)
    invariant(jid, 'Authenticate before sending event')
    const message = xml(
      'message',
      {
        type: 'groupchat',
        from: `${jid.local}@${jid.domain}`,
        to: `${to}@${MUC_DOMAIN}`,
        id: eventId,
      },
      xml('body', {}, escapeEvent(JSON.stringify(event))),
      xml('markable', MARKERS_XMLNS)
    )

    await sendElement('element', message)
  }

  const setMuteStatus = (roomId: string, isMuted: boolean) => {
    const jid = getJid(client)
    invariant(jid, 'Authenticate before sending event')

    const secondsInCentury = 60 * 60 * 24 * 365 * 100
    const query = xml(
      'iq',
      { type: 'set' },
      xml(
        'query',
        { xmlns: `${INBOX_XMLNS}#conversation`, jid: `${roomId}@${MUC_DOMAIN}` },
        xml('mute', {}, isMuted ? secondsInCentury.toString() : '0')
      )
    )

    return sendElement('iq', query)
  }

  const markEvent = async (
    to: string,
    eventId: string,
    status: EventStatus,
    markerId = nanoid()
  ) => {
    const jid = getJid(client)
    invariant(jid, 'Authenticate before sending event')
    const message = xml(
      'message',
      {
        type: 'groupchat',
        from: `${jid.local}@${jid.domain}`,
        to: `${to}@${MUC_DOMAIN}`,
        id: markerId,
      },
      xml(status, { id: eventId, xmlns: MARKERS_XMLNS })
    )

    await sendElement('element', message)
  }

  const stop = async () => {
    messagesQueue.forEach(({ onError }) => {
      onError(Error('client stopped'))
    })
    messagesQueue = []
    client?.removeAllListeners()
    await client?.stop()
  }

  const onStatusChange = (handler: (status: Status) => void) => {
    invariant(client, 'Authenticate before listening to status change')
    client.reconnect.on('reconnecting', () => {
      handler('RECONNECTING')
    })

    client.reconnect.on('reconnected', () => {
      handler('CONNECTED')
    })
  }

  const setupListeners = (listeners: Listeners) => {
    invariant(client, 'Authenticate before setting up listeners')

    if (listeners?.onConversationStatusChange) {
      onConversationStatusChange(client, listeners.onConversationStatusChange)
    }
    if (listeners?.onGroupchatMessage) {
      onGroupchatStanza(client, listeners.onGroupchatMessage)
    }
    if (listeners?.onGroupchatMarker) {
      onGroupchatMarkerStanza(client, listeners.onGroupchatMarker)
    }
    if (listeners?.onGroupchatMessageFailed) {
      onGroupchatFailedStanza(client, listeners.onGroupchatMessageFailed)
    }
    if (listeners?.onAffiliationChange) {
      onAffiliationsStanza(client, listeners.onAffiliationChange)
    }
    if (listeners?.onArchivedAffiliationsChange) {
      onArchivedAffiliationsStanza(client, listeners.onArchivedAffiliationsChange)
    }
  }

  const start = (): Promise<void> => {
    // TODO https://floatingapps.atlassian.net/browse/CPP-9869
    invariant(client, 'Authenticate before starting client')
    const currentClient = client

    return new Promise((resolve, reject) => {
      const errorHandler = (error: Error) => {
        currentClient.removeAllListeners()
        currentClient
          .stop()
          .catch(R.doNothing())
          .finally(() => reject(error))
      }
      currentClient.once('error', errorHandler)
      currentClient.once('connect', () => {
        currentClient.removeListener('error', errorHandler)
        resolve()
      })
      currentClient.start().catch(errorHandler)
    })
  }

  const onError = (errorHandler: (error: unknown, cleanupClient: () => Promise<void>) => void) => {
    // TODO https://floatingapps.atlassian.net/browse/CPP-9869
    invariant(client, 'Authenticate before starting client')
    const currentClient = client

    currentClient.once('error', error => {
      errorHandler(error, async () => {
        currentClient.removeAllListeners()
        await currentClient.stop().catch(R.doNothing())
      })
    })
  }

  const getAffiliations = async (
    roomId: string
  ): Promise<{
    version: string
    users: Array<{ id: string; affiliation: 'member' | 'none' | 'owner' }>
  }> => {
    invariant(client, 'Authenticate before getting roster')
    const query = xml(
      'iq',
      { type: 'get', to: `${roomId}@${MUC_DOMAIN}` },
      xml('query', { xmlns: MUC_LIGHT_AFFILIATIONS })
    )
    const stanza = await sendElement('iq', query)
    const version = stanza.getChild('query')?.getChild('version')?.getText()
    invariant(version, 'Version not found in the stanza')
    const users = stanza
      .getChild('query')
      ?.getChildren('user')
      .map(item => {
        const id = item.getText().split('@')[0]
        invariant(id, 'User ID not found in the stanza')
        return {
          id,
          affiliation: item.getAttr('affiliation') as 'member' | 'none' | 'owner',
        }
      })
    invariant(users, 'Users not found in the stanza')
    return { version, users }
  }

  return {
    loadInbox,
    loadRoomEvents,
    sendEvent,
    markEvent,
    auth,
    start,
    stop,
    onError,
    onStatusChange,
    setupListeners,
    loadInboxItem,
    getAffiliations,
    setMuteStatus,
  }
}
